import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
    ResponsiveContainer, BarChart, Bar, AreaChart, Area,
    XAxis, YAxis, Tooltip, CartesianGrid, Cell,
} from "recharts";
import { API_BASE } from "../../apiBase";

const API = API_BASE;
const CATEGORIES = ["Money Market", "Treasury Bonds", "Equities", "Currencies (FX)", "Crypto", "Commodities / Credits"];

/* Tonal green palette for the stacked area chart (Chart 3) — lightest to darkest */
const TONAL_COLORS = {
    Dealer: "#c8dcc4",
    Asset_Mgr: "#9abf94",
    Lev_Money: "#6fa368",
    Other_Rept: "#4a8843",
    NonRept: "#2d6628",
};
const P_LABELS = { Dealer: "Dealer", Asset_Mgr: "AM", Lev_Money: "LM", Other_Rept: "OthRept", NonRept: "NonRept" };
const PARTICIPANTS = ["Dealer", "Asset_Mgr", "Lev_Money", "Other_Rept", "NonRept"];

/* Shared teal color for all chart text (axes, legends, subtitles) — matches "Total Gross Contracts = 2 × OI" */
const TEAL = "var(--pord-teal, #b8d4d0)";

const README_TEXT = `In addition to open interest, we also track gross contracts — defined as the total number of long plus short contracts across all participants. Gross contracts are exactly twice as much of open interest, Long + Short. Presenting the data in gross-contract terms highlights how many contracts are actually sitting on each participant's book, and how those holdings evolve over time.`;

const fmtK = (v) => {
    if (v == null) return "—";
    return Math.round(v).toLocaleString();
};

const fmtDate = (d) => {
    if (!d) return "";
    const parts = d.split("-");
    return `${parts[1]}/${parts[2]}/${parts[0].slice(2)}`;
};

export default function GrossContracts() {
    const [contracts, setContracts] = useState([]);
    const [selectedCode, setSelectedCode] = useState("099741");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showReadMe, setShowReadMe] = useState(false);
    const [view1, setView1] = useState("ls");
    const [view2, setView2] = useState("ls");
    const [view3, setView3] = useState("ls");

    useEffect(() => {
        axios.get(`${API}/api/cot-pro/contracts`).then((r) => setContracts(r.data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!selectedCode) return;
        setLoading(true);
        let retries = 0;
        const load = () => {
            axios.get(`${API}/api/cot-pro/gross/${selectedCode}`)
                .then((r) => { setData(r.data); setLoading(false); })
                .catch((err) => {
                    if (err.response?.status === 503 && retries < 30) { retries++; setTimeout(load, 2000); return; }
                    setData(null);
                    setLoading(false);
                });
        };
        load();
    }, [selectedCode]);

    const catMap = useMemo(() => {
        const m = {};
        for (const c of contracts) {
            if (!m[c.category]) m[c.category] = [];
            m[c.category].push(c);
        }
        return m;
    }, [contracts]);

    const barData = useMemo(() => {
        if (!data?.bars) return [];
        return data.bars.map((b) => ({
            label: b.label, participant: b.participant,
            prior: view1 === "long" ? b.prior_long : view1 === "short" ? b.prior_short : view1 === "net" ? b.prior_net : b.prior,
            current: view1 === "long" ? b.current_long : view1 === "short" ? b.current_short : view1 === "net" ? b.current_net : b.current,
        }));
    }, [data, view1]);

    const changeData = useMemo(() => {
        if (!data?.bars) return [];
        return data.bars.map((b) => ({
            label: b.label, participant: b.participant,
            change: (view2 === "net" ? b.change_net
                : view2 === "long" ? b.change_long
                : view2 === "short" ? b.change_short
                : view2 === "spread" ? b.change_spread
                : b.change) || 0,
        }));
    }, [data, view2]);

    const stackedData = useMemo(() => {
        if (!data?.stacked_series) return [];
        return data.stacked_series.map((s) => {
            const point = { date: fmtDate(s.date), rawDate: s.date };
            if (view3 === "long") {
                const total = (s.Dealer_long || 0) + (s.Asset_Mgr_long || 0) + (s.Lev_Money_long || 0) + (s.Other_Rept_long || 0) + (s.NonRept_long || 0);
                point.Dealer = total > 0 ? ((s.Dealer_long || 0) / total) * 100 : 0;
                point.AM = total > 0 ? ((s.Asset_Mgr_long || 0) / total) * 100 : 0;
                point.LM = total > 0 ? ((s.Lev_Money_long || 0) / total) * 100 : 0;
                point.OthRept = total > 0 ? ((s.Other_Rept_long || 0) / total) * 100 : 0;
                point.NonRept = total > 0 ? ((s.NonRept_long || 0) / total) * 100 : 0;
                point._raw_Dealer = s.Dealer_long || 0; point._raw_AM = s.Asset_Mgr_long || 0;
                point._raw_LM = s.Lev_Money_long || 0; point._raw_OthRept = s.Other_Rept_long || 0;
                point._raw_NonRept = s.NonRept_long || 0; point._total = total;
            } else if (view3 === "short") {
                const total = (s.Dealer_short || 0) + (s.Asset_Mgr_short || 0) + (s.Lev_Money_short || 0) + (s.Other_Rept_short || 0) + (s.NonRept_short || 0);
                point.Dealer = total > 0 ? ((s.Dealer_short || 0) / total) * 100 : 0;
                point.AM = total > 0 ? ((s.Asset_Mgr_short || 0) / total) * 100 : 0;
                point.LM = total > 0 ? ((s.Lev_Money_short || 0) / total) * 100 : 0;
                point.OthRept = total > 0 ? ((s.Other_Rept_short || 0) / total) * 100 : 0;
                point.NonRept = total > 0 ? ((s.NonRept_short || 0) / total) * 100 : 0;
                point._raw_Dealer = s.Dealer_short || 0; point._raw_AM = s.Asset_Mgr_short || 0;
                point._raw_LM = s.Lev_Money_short || 0; point._raw_OthRept = s.Other_Rept_short || 0;
                point._raw_NonRept = s.NonRept_short || 0; point._total = total;
            } else {
                point.Dealer = s.Dealer_pct; point.AM = s.Asset_Mgr_pct; point.LM = s.Lev_Money_pct;
                point.OthRept = s.Other_Rept_pct; point.NonRept = s.NonRept_pct;
                point._raw_Dealer = s.Dealer; point._raw_AM = s.Asset_Mgr;
                point._raw_LM = s.Lev_Money; point._raw_OthRept = s.Other_Rept;
                point._raw_NonRept = s.NonRept; point._total = s.total;
            }
            return point;
        });
    }, [data, view3]);

    const getName = (code) => contracts.find((c) => c.code === code)?.name || code;
    const getNum = (code) => contracts.find((c) => c.code === code)?.num || "";

    return (
        <div data-testid="gross-contracts-tab" style={{ maxWidth: "66%", minWidth: 600, marginLeft: 40 }}>
            {/* Top controls */}
            <div className="flex items-center gap-4 mb-5 flex-wrap">
                <ContractSelector
                    catMap={catMap}
                    selectedCode={selectedCode}
                    onChange={setSelectedCode}
                    getNum={getNum}
                    getName={getName}
                />
                <div style={{ flex: 1 }} />
                <button
                    data-testid="gross-readme-btn"
                    onClick={() => setShowReadMe(true)}
                    style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "8px 22px", borderRadius: 999,
                        background: "rgba(184,212,208,0.12)", border: "1px solid var(--pord-teal)",
                        color: "var(--pord-teal)", fontSize: 15, fontWeight: 600, letterSpacing: "0.05em",
                        cursor: "pointer", transition: "all 200ms",
                        boxShadow: "0 0 12px rgba(184,212,208,0.15)",
                        fontFamily: "'DM Sans', sans-serif",
                    }}
                >
                    READ ME
                </button>
            </div>

            {/* Contract name + summary */}
            {data && (
                <div className="mb-5" style={{ display: "flex", alignItems: "baseline", gap: 14 }}>
                    <span style={{ fontSize: 22, fontWeight: 400, color: "#fff", fontFamily: "'DM Sans', sans-serif" }}>
                        {getNum(selectedCode)}. {getName(selectedCode)}
                    </span>
                    <span style={{ fontSize: 14, color: TEAL, fontFamily: "'DM Sans', sans-serif" }}>
                        Total Gross Contracts = 2 × OI = {fmtK(data.total_gross)}
                    </span>
                </div>
            )}

            {loading && <div style={{ color: "rgba(255,255,255,0.4)", padding: 40, textAlign: "center", fontFamily: "'DM Sans', sans-serif", fontSize: 15 }}>Loading…</div>}

            {data && !loading && (
                <div style={{ display: "flex", flexDirection: "column", gap: 32 }}>
                    {/* Chart 3: 6-Month Stacked Area — tonal greens, Dealer at bottom, translucent */}
                    <ChartCard title="6-Month Lookback" subtitle="% by Participant" toggle={<LookbackToggle value={view3} onChange={setView3} />}>
                        <ResponsiveContainer width="100%" height={340}>
                            <AreaChart data={stackedData}>
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,212,208,0.06)" />
                                <XAxis dataKey="date" tick={{ fill: "#b8d4d0", fontSize: 12, fontFamily: "'DM Sans', sans-serif" }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
                                <YAxis tick={{ fill: "#b8d4d0", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }} axisLine={false} tickLine={false} domain={[0, 100]} tickFormatter={(v) => `${v}%`} width={50} />
                                <Tooltip content={<GrossTooltip type="stacked" />} />
                                {[
                                    { key: "Dealer", color: TONAL_COLORS.Dealer },
                                    { key: "AM", color: TONAL_COLORS.Asset_Mgr },
                                    { key: "LM", color: TONAL_COLORS.Lev_Money },
                                    { key: "OthRept", color: TONAL_COLORS.Other_Rept },
                                    { key: "NonRept", color: TONAL_COLORS.NonRept },
                                ].map(({ key, color }) => (
                                    <Area key={key} type="monotone" dataKey={key} stackId="1" stroke={color} strokeOpacity={0.45} fill={color} fillOpacity={0.30} />
                                ))}
                            </AreaChart>
                        </ResponsiveContainer>
                        <div style={{ display: "flex", gap: 18, justifyContent: "center", marginTop: 8, flexWrap: "wrap" }}>
                            {PARTICIPANTS.map((p) => (
                                <LegendItem key={p} color={TONAL_COLORS[p]} label={P_LABELS[p]} />
                            ))}
                        </div>
                    </ChartCard>

                    {/* Chart 2: W/W Change Bar */}
                    <ChartCard title={`Gross Contracts — ${view2 === "net" ? "Net (L−S)" : view2 === "long" ? "L" : view2 === "short" ? "S" : view2 === "spread" ? "Spread" : "L+S"} w/w change`} toggle={<ViewToggle value={view2} onChange={setView2} />}>
                        <ResponsiveContainer width="100%" height={280}>
                            <BarChart data={changeData} barCategoryGap="22%">
                                <CartesianGrid strokeDasharray="3 3" stroke="rgba(184,212,208,0.06)" />
                                <XAxis dataKey="label" tick={{ fill: "#b8d4d0", fontSize: 13, fontFamily: "'DM Sans', sans-serif" }} axisLine={false} tickLine={false} />
                                <YAxis tick={{ fill: "#b8d4d0", fontSize: 14, fontFamily: "'DM Sans', sans-serif" }} axisLine={false} tickLine={false} tickFormatter={fmtK} width={60} />
                                <Tooltip content={<GrossTooltip type="change" />} cursor={{ fill: "rgba(184,212,208,0.04)" }} />
                                <Bar dataKey="change" name="w/w Change" radius={[2, 2, 0, 0]}>
                                    {changeData.map((entry, i) => (
                                        <Cell key={i} fill={entry.change >= 0 ? "#b8d4d0" : "#c47070"} fillOpacity={0.35} stroke={entry.change >= 0 ? "#b8d4d0" : "#c47070"} strokeOpacity={0.50} />
                                    ))}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    </ChartCard>
                </div>
            )}

            {/* READ ME Modal */}
            {showReadMe && (
                <div onClick={() => setShowReadMe(false)} style={{
                    position: "fixed", inset: 0, zIndex: 1000,
                    background: "rgba(0,0,0,0.7)", backdropFilter: "blur(4px)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                    <div data-testid="gross-readme-modal" onClick={(e) => e.stopPropagation()} style={{
                        background: "#2a2e22", border: "1px solid rgba(184,212,208,0.15)",
                        borderRadius: 14, padding: "34px 38px", maxWidth: 640, width: "90%",
                        boxShadow: "0 8px 40px rgba(0,0,0,0.5)",
                    }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
                            <span style={{ fontSize: 20, fontWeight: 500, color: "var(--pord-teal)", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.04em" }}>
                                GROSS CONTRACTS — READ ME
                            </span>
                            <button onClick={() => setShowReadMe(false)} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 24, cursor: "pointer", lineHeight: 1 }}>×</button>
                        </div>
                        <p style={{ fontSize: 16, lineHeight: 1.8, color: "rgba(255,255,255,0.78)", fontFamily: "'DM Sans', sans-serif" }}>
                            {README_TEXT}
                        </p>
                        <div style={{ marginTop: 16, padding: "14px 20px", background: "rgba(184,212,208,0.06)", borderRadius: 8, border: "1px solid rgba(184,212,208,0.10)" }}>
                            <span style={{ fontSize: 15, color: "var(--pord-teal)", fontFamily: "'DM Sans', sans-serif", fontWeight: 500 }}>
                                Total Gross Contracts = Long + Short = 2 × Open Interest
                            </span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ── Sub-components ── */

function ContractSelector({ catMap, selectedCode, onChange, getNum, getName }) {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);

    useEffect(() => {
        const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    return (
        <div ref={ref} style={{ position: "relative" }}>
            <button
                data-testid="gross-contract-selector"
                onClick={() => setOpen(!open)}
                style={{
                    fontFamily: "'DM Sans', sans-serif", fontSize: 15, padding: "10px 20px", borderRadius: 8,
                    background: "rgba(184,212,208,0.06)", border: "1px solid rgba(184,212,208,0.18)",
                    color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 10,
                    minWidth: 300,
                }}
            >
                <span style={{ color: "var(--pord-teal)", fontWeight: 500 }}>{getNum(selectedCode)}.</span>
                <span>{getName(selectedCode)}</span>
                <span style={{ marginLeft: "auto", fontSize: 12, color: "rgba(255,255,255,0.35)" }}>▼</span>
            </button>
            {open && (
                <div data-testid="gross-contract-dropdown" style={{
                    position: "absolute", top: "100%", left: 0, marginTop: 6,
                    background: "#2a2e22", border: "1px solid rgba(184,212,208,0.18)",
                    borderRadius: 10, width: 400, maxHeight: 520, overflowY: "auto", zIndex: 100,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
                }}>
                    {CATEGORIES.map((cat) => {
                        const items = catMap[cat] || [];
                        if (!items.length) return null;
                        return (
                            <div key={cat}>
                                <div style={{ padding: "10px 16px", fontSize: 12, fontWeight: 600, color: "var(--pord-teal)", letterSpacing: "0.08em", textTransform: "uppercase", fontFamily: "'DM Sans', sans-serif", background: "rgba(184,212,208,0.04)", borderBottom: "1px solid rgba(184,212,208,0.06)" }}>
                                    {cat}
                                </div>
                                {items.filter((c) => c.has_data).map((c) => (
                                    <button
                                        key={c.code}
                                        onClick={() => { onChange(c.code); setOpen(false); }}
                                        style={{
                                            display: "block", width: "100%", textAlign: "left",
                                            padding: "9px 16px", fontSize: 14, fontFamily: "'DM Sans', sans-serif",
                                            background: c.code === selectedCode ? "rgba(184,212,208,0.10)" : "transparent",
                                            border: "none", color: c.code === selectedCode ? "#fff" : "rgba(255,255,255,0.6)",
                                            cursor: "pointer", transition: "background 100ms",
                                        }}
                                        onMouseEnter={(e) => { e.currentTarget.style.background = "rgba(184,212,208,0.08)"; }}
                                        onMouseLeave={(e) => { e.currentTarget.style.background = c.code === selectedCode ? "rgba(184,212,208,0.10)" : "transparent"; }}
                                    >
                                        <span style={{ color: "var(--pord-teal)", marginRight: 8 }}>{c.num}.</span>{c.name}
                                    </button>
                                ))}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}

function ChartCard({ title, subtitle, toggle, children }) {
    return (
        <div style={{
            background: "rgba(184,212,208,0.03)", border: "1px solid rgba(184,212,208,0.08)",
            borderRadius: 12, padding: "20px 20px 16px",
        }}>
            <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                    <span style={{ fontSize: 16, fontWeight: 400, color: "#DAF2D0", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.03em" }}>
                        {title}
                    </span>
                    {subtitle && (
                        <span style={{ fontSize: 13, color: "#b8d4d0", fontFamily: "'DM Sans', sans-serif" }}>
                            {subtitle}
                        </span>
                    )}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    {toggle}
                    <span style={{ fontSize: 15, color: "rgba(184,212,208,0.60)", fontFamily: "'DM Sans', sans-serif", letterSpacing: "0.02em", whiteSpace: "nowrap" }}>
                        Total Gross Contracts = 2 × OI
                    </span>
                </div>
            </div>
            {children}
        </div>
    );
}

function ViewToggle({ value, onChange }) {
    const opts = [
        { key: "ls", label: "L+S" },
        { key: "long", label: "L" },
        { key: "short", label: "S" },
        { key: "spread", label: "Spread" },
        { key: "net", label: "Net" },
    ];
    return (
        <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid rgba(184,212,208,0.18)" }}>
            {opts.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)}
                    style={{
                        padding: "4px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif",
                        background: value === o.key ? "rgba(184,212,208,0.15)" : "transparent",
                        border: "none", color: value === o.key ? "#fff" : "rgba(255,255,255,0.45)",
                        cursor: "pointer", fontWeight: value === o.key ? 600 : 400,
                        transition: "all 150ms",
                    }}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function LookbackToggle({ value, onChange }) {
    const opts = [
        { key: "ls", label: "(L+Spread)+(S+Spread)" },
        { key: "long", label: "L+Spread" },
        { key: "short", label: "S+Spread" },
    ];
    return (
        <div style={{ display: "flex", borderRadius: 6, overflow: "hidden", border: "1px solid rgba(184,212,208,0.18)" }}>
            {opts.map((o) => (
                <button key={o.key} onClick={() => onChange(o.key)}
                    style={{
                        padding: "4px 12px", fontSize: 12, fontFamily: "'DM Sans', sans-serif",
                        background: value === o.key ? "rgba(184,212,208,0.15)" : "transparent",
                        border: "none", color: value === o.key ? "#fff" : "rgba(255,255,255,0.45)",
                        cursor: "pointer", fontWeight: value === o.key ? 600 : 400,
                        transition: "all 150ms",
                    }}>
                    {o.label}
                </button>
            ))}
        </div>
    );
}

function LegendItem({ color, label }) {
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ width: 12, height: 12, borderRadius: 2, background: color }} />
            <span style={{ fontSize: 13, color: "#b8d4d0", fontFamily: "'DM Sans', sans-serif" }}>{label}</span>
        </div>
    );
}

function GrossTooltip({ active, payload, label, type }) {
    if (!active || !payload?.length) return null;
    return (
        <div style={{
            background: "rgba(22,25,18,0.95)", border: "1px solid rgba(184,212,208,0.20)",
            borderRadius: 8, padding: "12px 16px", fontSize: 14, fontFamily: "'DM Sans', sans-serif",
            boxShadow: "0 6px 24px rgba(0,0,0,0.5)",
        }}>
            <div style={{ color: "var(--pord-teal)", fontWeight: 500, marginBottom: 6, fontSize: 15 }}>{label}</div>
            {type === "bar" && payload.map((p, i) => (
                <div key={i} style={{ color: p.fill || p.color, marginBottom: 3 }}>
                    {p.name}: <span style={{ color: "#fff", fontWeight: 500 }}>{fmtK(p.value)}</span>
                </div>
            ))}
            {type === "change" && payload.map((p, i) => (
                <div key={i} style={{ color: p.value >= 0 ? "#A8D8A0" : "#C47070" }}>
                    w/w: <span style={{ fontWeight: 500 }}>{p.value >= 0 ? "+" : ""}{fmtK(p.value)}</span>
                </div>
            ))}
            {type === "stacked" && (() => {
                const d = payload[0]?.payload;
                const total = d?._total;
                const participants = ["Dealer", "AM", "LM", "OthRept", "NonRept"];
                return (
                    <>
                        {total != null && (
                            <div style={{ color: "var(--pord-teal)", fontWeight: 500, marginBottom: 6, fontSize: 13, borderBottom: "1px solid rgba(184,212,208,0.15)", paddingBottom: 5 }}>
                                Total Gross: <span style={{ color: "#fff" }}>{Math.round(total).toLocaleString()}</span>
                            </div>
                        )}
                        {participants.map((name) => {
                            const raw = d?.[`_raw_${name}`];
                            const pct = d?.[name];
                            return (
                                <div key={name} style={{ display: "flex", justifyContent: "space-between", gap: 16, color: "#d4e8d0", marginBottom: 2 }}>
                                    <span>{name}</span>
                                    <span style={{ color: "#fff", fontWeight: 500 }}>{raw != null ? Math.round(raw).toLocaleString() : "—"}<span style={{ color: "rgba(255,255,255,0.4)", fontWeight: 400, marginLeft: 6 }}>{pct?.toFixed(1)}%</span></span>
                                </div>
                            );
                        })}
                    </>
                );
            })()}
        </div>
    );
}
